import { format } from 'date-fns';
import { Clock, Edit, Trash2, UserX } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader } from '@/components/ui/card';
import type { Appointment } from '@/types/appointment';

export type SlotCardVariant = 'hero' | 'admin';

interface SlotCardProps {
  appointment: Appointment;
  variant: SlotCardVariant;
  onClaim?: (appointment: Appointment) => void;
  onRelease?: (appointment: Appointment) => void;
  onEdit?: (appointment: Appointment) => void;
  onDelete?: (appointment: Appointment) => void;
  onUnassign?: (appointment: Appointment) => void;
  busy?: boolean;
}

function formatSlotTime(startTime: string, duration: number) {
  const start = new Date(startTime);
  return `${format(start, 'EEE, MMM d, yyyy - HH:mm')} (${duration} minutes)`;
}

export function AppointmentCard({
  appointment,
  variant,
  onClaim,
  onRelease,
  onEdit,
  onDelete,
  onUnassign,
  busy = false,
}: SlotCardProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-base font-semibold text-primary">
              <Clock className="h-4 w-4" />
              <span>{formatSlotTime(appointment.startTime, appointment.duration)}</span>
            </div>
          </div>

          {variant === 'hero' && (
            <div className="flex shrink-0 items-center gap-2">
              {appointment.mine ? (
                <>
                  <Badge className="border-border bg-accent font-display tracking-wide text-accent-foreground">
                    Your mission!
                  </Badge>
                  {onRelease && (
                    <Button variant="outline" size="sm" disabled={busy} onClick={() => onRelease(appointment)}>
                      Release
                    </Button>
                  )}
                </>
              ) : (
                onClaim && (
                  <Button size="sm" disabled={busy} onClick={() => onClaim(appointment)}>
                    I'm in!
                  </Button>
                )
              )}
            </div>
          )}

          {variant === 'admin' && (
            <div className="flex shrink-0 items-center gap-2">
              {appointment.assignedToName ? (
                <Badge variant="secondary">Hero: {appointment.assignedToName}</Badge>
              ) : (
                <span className="text-sm text-muted-foreground">Open — no hero yet</span>
              )}
              {appointment.assignedToName && onUnassign && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => onUnassign(appointment)}
                  className="gap-1"
                >
                  <UserX className="h-4 w-4" />
                  Unassign
                </Button>
              )}
              {onEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => onEdit(appointment)}
                  className="gap-1"
                >
                  <Edit className="h-4 w-4" />
                  Edit
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => onDelete(appointment)}
                  className="gap-1"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              )}
            </div>
          )}
        </div>
      </CardHeader>
    </Card>
  );
}
